import * as React from "react";
import { format, isValid, parseISO } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function parseDateValue(value) {
  if (!value) return undefined;
  const raw = String(value).slice(0, 10);
  if (!raw) return undefined;
  const parsed = parseISO(raw);
  return isValid(parsed) ? parsed : undefined;
}

function toDateString(date) {
  if (!date || !isValid(date)) return "";
  return format(date, "yyyy-MM-dd");
}

const DateInput = React.forwardRef(
  ({ className, value, onChange, onBlur, disabled, placeholder = "Pick a date", id, name, ...props }, ref) => {
    const [open, setOpen] = React.useState(false);
    const selected = parseDateValue(value);

    const handleSelect = (date) => {
      const next = toDateString(date);
      onChange?.({ target: { value: next, name } });
      if (next) setOpen(false);
    };

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            type="button"
            variant="outline"
            id={id}
            name={name}
            disabled={disabled}
            onBlur={onBlur}
            className={cn(
              "h-9 w-full justify-start px-3 text-left font-normal",
              !selected && "text-muted-foreground",
              className
            )}
            {...props}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-60" />
            {selected ? format(selected, "MMM d, yyyy") : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={handleSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    );
  }
);

DateInput.displayName = "DateInput";

export { DateInput };
